// components/slides/slidesAgentContext.ts
import { describeBrandResearch } from '#lib/slides/brandResearch';
//
// Builds the Reviewer agent's live deck context for a slides turn (mirrors
// components/tables/tablesAgentContext.ts for the Analyst): the deck outline
// (pages, labels, sizes, extracted text, existing annotations), the review
// state, and the page snapshots to attach as vision inputs — the focused page
// plus any pages the agent requested with SLIDES_READ_PAGE last pass.

import type { LlmTurnImage, SlideDeckDTO, SlideElement, SlidePageDoc } from '#types/types';
import { reservedExportRegions, TYPICAL_NOTE } from '#lib/slides/annotationLayout';
import { SLIDES_VALIDATION_POINTS } from '#lib/slides/validationPoints';
import { useSlidesStore, resolveAgentSlideDeck } from './slidesStore';
import { countFreeNoteSlots } from './inkProbe';

const MAX_PAGE_TEXT_CHARS = 2600;
const MAX_VISION_PAGES = 4;
/** Cap per page so a collage slide can't flood the turn with attachments. */
const MAX_IMAGES_PER_PAGE = 3;

const describeElement = (el: SlideElement): string => {
  const at = `@(${Math.round(el.frame.x)},${Math.round(el.frame.y)} ${Math.round(el.frame.w)}x${Math.round(el.frame.h)})`;
  const point = el.guidelinePointId ? ` [${el.guidelinePointId}]` : '';
  const text = 'text' in el && el.text ? ` "${String(el.text).slice(0, 140)}"` : '';
  const severity = el.type === 'note' && el.severity ? ` (${el.severity})` : '';
  // Image elements carry their source url — surface it so the agent knows the
  // picture exists (and that it is attachable) instead of seeing a bare frame.
  const src = el.type === 'image' && el.url ? ` src=${el.url}` : '';
  return `    - ${el.id} ${el.type}${severity}${point} ${at}${text}${src}`;
};

/** Vision inputs for one page: the imported PDF snapshot when there is one,
 *  otherwise the page's own image elements. Authored pages (kind 'blank') never
 *  get a snapshotUrl — without this fallback an image dropped onto a slide is
 *  invisible to the agent, which then reports "no rendered snapshot" forever. */
const pageVisionImages = (page: SlidePageDoc, deckName: string): LlmTurnImage[] => {
  const where = `Slides page ${page.index + 1}${page.label ? `: ${page.label}` : ''} (deck "${deckName}")`;
  // Prefer the coordinate-gridded snapshot when available so the agent reads anchor
  // {x,y} off the labelled grid (page points) instead of eyeballing pixels.
  if (page.gridSnapshotUrl) {
    return [{ url: page.gridSnapshotUrl, label: `${where}: with a PAGE-POINT COORDINATE GRID overlaid; read anchor x,y off the labelled grid lines` }];
  }
  if (page.snapshotUrl) return [{ url: page.snapshotUrl, label: where }];
  return page.elements
    .filter((el): el is Extract<SlideElement, { type: 'image' }> => el.type === 'image' && !!el.url)
    .slice(0, MAX_IMAGES_PER_PAGE)
    .map((el) => ({ url: el.url, label: `${where}: image element ${el.id}` }));
};

/** Positioned labels for the focused page — the deterministic anchor source.
 *  Capped so a dimension-heavy plan doesn't flood the prompt. */
const MAX_TEXT_ANCHORS = 80;
const describeTextAnchors = (page: SlidePageDoc): string | null => {
  if (!page.textMap || page.textMap.length === 0) return null;
  const rows = page.textMap
    .slice(0, MAX_TEXT_ANCHORS)
    .map((i) => `"${i.t}"@(${Math.round(i.x)},${Math.round(i.y)})`)
    .join('  ');
  return `TEXT ANCHORS on the focused page (label to exact page-point centre; anchor callouts by NAME with anchorText, exact and preferred over reading the grid): ${rows}`;
};

const describePage = (page: SlidePageDoc, isActive: boolean): string => {
  if (page.cover) return `Page ${page.index + 1} [id ${page.id}]: REVIEW COVER, the reviewer's own first page (validation stamp, Better Places, Tenant Survey, cartouche). It is not part of the tenant's proposal: never review, label or annotate it.`;
  const lines: string[] = [];
  const label = page.label ? `: "${page.label}"` : ': (unlabeled)';
  const bg = page.background ? `, bg ${page.background}` : '';
  lines.push(
    `Page ${page.index + 1} [id ${page.id}]${label}: ${page.kind}, ${Math.round(page.width)}x${Math.round(page.height)}pt${bg}${isActive ? '  << FOCUSED (image attached this turn)' : ''}`
  );
  const text = (page.textContent ?? '').trim();
  if (text) {
    const truncated = text.length > MAX_PAGE_TEXT_CHARS;
    lines.push(`  text: ${text.slice(0, MAX_PAGE_TEXT_CHARS).replace(/\s+/g, ' ')}${truncated ? ' …[truncated]' : ''}`);
  } else {
    lines.push('  text: (none extracted; likely a pure graphic piece; view it to judge)');
  }
  if (page.elements.length > 0) {
    // Listed in PAINT ORDER: later entries draw on top, in the editor and in
    // the exported PDF alike (SLIDES_REORDER_ELEMENT changes this).
    lines.push(`  elements (${page.elements.length}, back-to-front):`);
    page.elements.forEach((el) => lines.push(describeElement(el)));
  }
  return lines.join('\n');
};

/** Prior-round archives → the V(n-1) context the Reviewer diffs against. */
const describeRounds = (deck: SlideDeckDTO): string | null => {
  const rounds = deck.doc.rounds;
  if (!rounds || rounds.length === 0) return null;
  const prev = rounds[rounds.length - 1]!;
  const lines: string[] = [
    `PREVIOUS ROUND (R${prev.round}): the tenant RESUBMITTED; you are now reviewing round ${deck.reviewRound}. MANDATE: diff this version against R${prev.round}; state which findings are RESOLVED, restate the ones that PERSIST (do not soften them), and flag every graphic piece present in R${prev.round} that has DISAPPEARED from the resubmission (require the complete file again).`,
  ];
  if (prev.review) {
    const pts = Object.entries(prev.review.points ?? {})
      .map(([pid, p]) => `${pid}:${p.verdict}${p.comment ? ` (${p.comment})` : ''}`)
      .join('; ');
    if (pts) lines.push(`  R${prev.round} verdicts: ${pts}`);
    if (prev.review.status) lines.push(`  R${prev.round} status: ${prev.review.status}`);
    if (prev.review.missingPieces?.length) lines.push(`  R${prev.round} missing pieces: ${prev.review.missingPieces.join('; ')}`);
    if (prev.review.synthesis) lines.push(`  R${prev.round} synthesis: ${prev.review.synthesis}`);
  }
  if (prev.pageLabels.length > 0) {
    lines.push(`  R${prev.round} pieces: ${prev.pageLabels.map((p) => `p.${p.index + 1}${p.label ? ` "${p.label}"` : ''}`).join(', ')}`);
  }
  if (prev.findings.length > 0) {
    lines.push(`  R${prev.round} findings (${prev.findings.length}):`);
    prev.findings.slice(0, 40).forEach((f) =>
      lines.push(`    - [page ${f.pageIndex + 1}${f.guidelinePointId ? `, ${f.guidelinePointId}` : ''}] ${f.text.slice(0, 180)}`)
    );
  }
  if (rounds.length > 1) {
    lines.push(`  (Older rounds on file: ${rounds.slice(0, -1).map((r) => `R${r.round} status ${r.review?.status ?? 'none'}`).join(', ')}.)`);
  }
  return lines.join('\n');
};

/** Deterministic p10 completeness pre-check: the required submission pieces
 *  (guidelines "Design submissions" page) matched against page labels + text.
 *  Deterministic register — same deck, same checklist, every turn. */
const REQUIRED_PIECES: ReadonlyArray<{ piece: string; patterns: RegExp }> = [
  { piece: 'Location / general arrangement & demolition plan', patterns: /(location|demolition|general arrangement|estado actual|plan masse|amenagement|aménagement|layout|floor plan|plano)/i },
  { piece: 'Reflected ceiling plan & lighting', patterns: /(reflected ceiling|rcp|plafond|techo|ceiling plan|lighting|iluminaci|éclairage|eclairage)/i },
  { piece: 'Material board (with references)', patterns: /(material|matériaux|materiaux|materiales|finishes|revestimiento|rev[eê]tement|pavimento|board)/i },
  { piece: 'Facade concept design (3D + elevation)', patterns: /(facade|façade|fachada|elevation|alzado|shopfront|storefront|3d|perspective|render)/i },
  { piece: 'Facade detail design (sections, 3 m zone)', patterns: /(section|coupe|secci|detail|détail|detalle)/i },
  { piece: 'Shopfront signage details', patterns: /(signage|enseigne|rotulo|rótulo|sign|logo|lettering)/i },
  { piece: 'Visual merchandising, furniture & fixtures', patterns: /(visual merchandising|\bvm\b|furniture|mobilier|mobiliario|fixtures|escaparate|vitrine)/i },
];

const describeCompleteness = (deck: SlideDeckDTO): string => {
  const pages = deck.doc.pages;
  const rows = REQUIRED_PIECES.map(({ piece, patterns }) => {
    const byLabel = pages.find((p) => p.label && patterns.test(p.label));
    if (byLabel) return `  [PRESENT] ${piece}: page ${byLabel.index + 1} ("${byLabel.label}")`;
    const byText = pages.find((p) => p.textContent && patterns.test(p.textContent));
    if (byText) return `  [UNCERTAIN] ${piece}: keyword hit on page ${byText.index + 1}; view the page to confirm before counting it`;
    return `  [MISSING] ${piece}: no page matches; request it (p10) unless you see it on an unlabeled page`;
  });
  return [
    'SUBMISSION COMPLETENESS PRE-CHECK (deterministic, from page labels + extracted text; confirm UNCERTAIN rows visually; label pages in Pass 0 to sharpen it):',
    ...rows,
  ].join('\n');
};

const describeReview = (deck: SlideDeckDTO): string => {
  const review = deck.doc.review;
  if (!review) return 'Review state: not started (no verdicts set yet).';
  const lines: string[] = [`Review state (round ${review.round}, guideline ${review.guidelineKey}):`];
  SLIDES_VALIDATION_POINTS.forEach(({ id, position, en }) => {
    const p = review.points[id];
    lines.push(`  row ${position} (${id}) ${en}: ${p ? `${p.verdict}${p.comment ? `: ${p.comment}` : ''}` : '(not set)'}`);
  });
  if (review.missingPieces?.length) lines.push(`  missing pieces: ${review.missingPieces.join('; ')}`);
  if (review.synthesis) lines.push(`  synthesis: ${review.synthesis}`);
  return lines.join('\n');
};

export interface SlidesAgentTurnContext {
  situation: string;
  images: LlmTurnImage[];
  guidelineKey?: string;
}

/**
 * How many more callouts the focused page can show WITHOUT covering the drawing
 * or another bubble, as a number the Reviewer can plan against.
 *
 * A dense sheet is the case this exists for. Told only "be concise", the agent
 * wrote a finding per guideline point and the page came back with bubbles
 * stacked over the artwork and over each other: every one individually correct,
 * the whole unreadable. Told "this page fits about 3", it can choose the three
 * that matter, which is what a human reviewer does on a crowded drawing.
 */
const describeLayoutBudget = async (
  deck: SlideDeckDTO,
  page: SlidePageDoc
): Promise<string | null> => {
  // Measured against an EMPTY sheet: this is the page's total allowance, which
  // is what the command layer enforces. Measuring against the current page would
  // give "how many more fit", and a remainder cannot be compared to a total.
  const blockers = reservedExportRegions(deck.doc.review?.stamp, deck.doc.pages.findIndex((p) => p.id === page.id), page.width, page.height);
  const total = await countFreeNoteSlots(page, TYPICAL_NOTE, blockers, page.width, page.height);
  if (total === null) return null;
  const placed = page.elements.filter((e) => e.type === 'note').length;
  const free = Math.max(0, total - placed);
  if (free === 0) {
    return `LAYOUT BUDGET, focused page: FULL. This sheet should carry at most ${total} callout(s) and ${placed} are already placed. Do not add more notes to this page; if something here matters more than what is already there, edit or remove an existing note instead.`;
  }
  return `LAYOUT BUDGET, focused page: this sheet should carry at most ${total} callout(s) in TOTAL, ${placed} are already placed, so you may add ${free} more. That is a HARD LIMIT the app enforces, not a suggestion. It is set by how much of this sheet is drawing: a dense page gets fewer, because comments crammed around a drawing stop being readable. If you have more findings than the budget, KEEP ONLY THE MOST IMPORTANT ones on the page and say the rest in the synthesis, or merge two related ones into a single clearer comment. Fewer, well-placed, readable comments beat a complete but illegible page.`;
};

const describeReviewLanguage = (language: 'fr' | 'en' | undefined): string => {
  if (!language) return '';
  const name = language === 'fr' ? 'French' : 'English';
  return `REVIEW LANGUAGE: ${name}. Write the callouts, the stamp comments and the synthesis in ${name} and set the review language to '${language}', whatever language the dossier is in.`;
};

export const buildSlidesAgentTurnContext = async (
  projectId?: string | null,
  // The Daimon reads the deck too; she must neither light the Reviewer's glow nor take the pages
  // the Reviewer asked to see on its next pass.
  options?: { observe?: boolean },
): Promise<SlidesAgentTurnContext> => {
  const deck = resolveAgentSlideDeck(projectId);
  if (!deck) {
    return {
      situation:
        'No deck is open in the Slides tool. Ask the user to open or import a proposal (PDF) before reviewing.',
      images: [],
    };
  }

  const st = useSlidesStore.getState();
  if (!options?.observe) st.markAgentActive();
  const runtime = st.runtime[deck.projectId] ?? {};
  const activePageId = runtime.activePageId ?? deck.doc.pages[0]?.id;

  // Vision: pages explicitly requested last pass (SLIDES_READ_PAGE) first,
  // then the focused page; capped to keep the turn lean.
  const requested = options?.observe ? [] : st.consumeVisionRequests();
  const visionIds = Array.from(new Set([...requested, ...(activePageId ? [activePageId] : [])])).slice(
    0,
    MAX_VISION_PAGES
  );
  const deckName = deck.sourceFileName ?? deck.projectId;
  const images: LlmTurnImage[] = [];
  visionIds.forEach((pid) => {
    const page = deck.doc.pages.find((p) => p.id === pid);
    if (page) images.push(...pageVisionImages(page, deckName));
  });

  // What the user has selected on the canvas — without this the agent could
  // not act on "make this one bigger" while an element was visibly selected.
  const selectedIds = runtime.selectedElementIds ?? [];
  const selectedLine = (() => {
    if (selectedIds.length === 0) return 'User selection: nothing selected on the canvas.';
    const found = deck.doc.pages.flatMap((p) =>
      p.elements
        .filter((e) => selectedIds.includes(e.id))
        .map((e) => `${e.type} ${e.id} (page ${p.index + 1})`)
    );
    return found.length > 0
      ? `User selection (what "this" refers to): ${found.join(', ')}.`
      : 'User selection: nothing selected on the canvas.';
  })();

  const presentation = deck.doc.kind === 'presentation';
  const header = [
    presentation
      ? `PRESENTATION deck, ${deck.doc.pages.length} page(s)${deck.doc.origin ? `, sent from Plan: scenario "${deck.doc.origin.scenarioName}" of plan "${deck.doc.origin.planName}" on ${deck.doc.origin.propertyName}, ${deck.doc.origin.sentAt.slice(0, 10)}` : ''}. This is NOT a tenant proposal to review: no guideline verdicts, no review stamp, no review. Your job is to help the team turn it into the presentation decision makers will see: edit, add and lay out pages (text, images, boxes) on request, keep figures exactly as sent unless the user changes them, and say so when a figure has no source.`
      : `Deck: "${deck.sourceFileName ?? 'untitled deck'}": ${deck.doc.pages.length} page(s), guideline ${deck.guidelineKey}, review round ${deck.reviewRound}, status ${deck.status}.`,
    selectedLine,
    activePageId
      ? `Focused page: ${deck.doc.pages.find((p) => p.id === activePageId)?.index != null ? deck.doc.pages.find((p) => p.id === activePageId)!.index + 1 : '?'} [id ${activePageId}].`
      : 'No page focused.',
    images.length > 0
      ? `Attached page image(s) this turn: ${images.map((i) => i.label).join(' | ')}.`
      : 'No page image attached this turn; use SLIDES_READ_PAGE to view a page before judging it visually.',
  ].join('\n');

  const pages = deck.doc.pages.map((p) => describePage(p, p.id === activePageId)).join('\n');
  const roundsBlock = describeRounds(deck);
  const activePage = deck.doc.pages.find((p) => p.id === activePageId);
  const anchorsBlock = activePage ? describeTextAnchors(activePage) : null;
  // Best-effort: no snapshot, or a probe that cannot decode, simply omits the
  // budget rather than blocking the turn.
  const budgetBlock = activePage ? await describeLayoutBudget(deck, activePage).catch(() => null) : null;

  return {
    situation: [
      header,
      st.brandResearchRun?.projectId === deck.projectId
        ? `BRAND RESEARCH IN PROGRESS: phase ${st.brandResearchRun.phase}, searches ${st.brandResearchRun.searches}/3, brand ${st.brandResearchRun.brand?.name ?? 'not yet identified'}. Research only; no review or annotation. ${st.brandResearchRun.phase !== 'complete' ? 'Identify the brand, search web first, read internal brand context afterward, collect references, then save brief.' : st.brandResearchRun.thenReview ? 'Brief already saved. The review starts automatically right after this turn: summarise the brief in two or three lines and do not ask the user to launch it.' : 'Brief already saved and shown in chat. Acknowledge completion briefly and wait for Launch review.'}`
        : deck.doc.brandResearch?.status === 'invalidated' ? describeBrandResearch(deck.doc.brandResearch) : '',
      describeReviewLanguage(st.runtime[deck.projectId]?.reviewLanguage ?? deck.doc.review?.language),
      describeReview(deck),
      describeCompleteness(deck),
      ...(roundsBlock ? [roundsBlock] : []),
      ...(budgetBlock ? [budgetBlock] : []),
      ...(anchorsBlock ? [anchorsBlock] : []),
      `PAGES:\n${pages}`,
    ].join('\n\n'),
    images,
    guidelineKey: deck.guidelineKey,
  };
};
